import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, IconButton, LinearProgress, Paper, Slide, Stack, TextField, Tooltip, Typography } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Device } from "@twilio/voice-sdk";
import { toast } from "react-toastify";
import { getData, postData } from "../../../apis/api";
import { ENDPOINTS } from "../../../apis/urls";
import { listenForPush } from "../../../hooks/useFcmRegistration";
import { unlockAudio, startSynthesizedRingtone, stopSynthesizedRingtone } from "../../../utils/callRingtone";
import CallHistoryTheme from "./CallHistoryTheme";
import CallerBrief, { CallerFacts, CallerIdentity, CallReason } from "./CallerBrief";

const API = ENDPOINTS.CALLS;
const NOTES_TAB = 3;

const clock = (from, now) => {
  const total = Math.max(0, Math.floor((now - from) / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

export default function HandoverPickupProvider() {
  const [rings, setRings] = useState([]);
  const [brief, setBrief] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [blocked, setBlocked] = useState(true);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneOpen, setPhoneOpen] = useState(false);
  // The claimed call keeps its own row, so the caller's details survive the ring closing.
  const [session, setSession] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const audio = useRef(null);
  const device = useRef(null);
  const twilioCall = useRef(null);
  const dismissed = useRef(new Set());
  const alive = useRef(true);
  const requesting = useRef(false);
  const audioWanted = useRef(false);
  const inCall = useRef(false);
  const ringWindow = useRef({});
  // A notification click lands on /calls?pickup=<id>; surface that call first.
  const preferred = useRef(typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("pickup"));

  const candidates = rings.filter((row) => !dismissed.current.has(row.handoverId));
  const ring = candidates.find((row) => String(row.callId) === preferred.current) || candidates[0] || null;
  audioWanted.current = !!ring && ring.state === "ringing-portal" && !busy && !session;
  inCall.current = !!session;

  const refresh = useCallback(async () => {
    if (requesting.current) return;
    requesting.current = true;
    try {
      const data = await getData(API.HANDOVER_RINGING);
      if (alive.current) setRings(data.Content || []);
    } catch (_) { /* A missed poll is retried; do not dismiss a live ring. */ }
    finally { requesting.current = false; }
  }, []);

  useEffect(() => {
    alive.current = true;
    audio.current = new Audio("/call-ringtone.wav");
    audio.current.loop = true;
    const unlock = () => {
      unlockAudio().catch(() => {});
      const element = audio.current;
      if (element) {
        element.muted = !audioWanted.current;
        element.play().then(() => {
          if (!audioWanted.current) { element.pause(); element.currentTime = 0; }
          element.muted = false; setBlocked(false);
        }).catch(() => { element.muted = false; setBlocked(false); });
      } else {
        setBlocked(false);
      }
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    refresh();
    const poll = setInterval(refresh, 5000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    let unsubscribe = () => {};
    listenForPush((payload) => { if (payload.data?.type?.startsWith("handover.")) refresh(); })
      .then((off) => { if (!alive.current) off(); else unsubscribe = off; }).catch(() => {});
    const wake = () => refresh();
    window.addEventListener("focus", wake);
    navigator.serviceWorker?.addEventListener("message", wake);
    return () => {
      alive.current = false;
      clearInterval(poll); clearInterval(tick); unsubscribe();
      window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock);
      window.removeEventListener("focus", wake);
      navigator.serviceWorker?.removeEventListener("message", wake);
      audio.current?.pause(); stopSynthesizedRingtone(); device.current?.destroy();
    };
  }, [refresh]);

  const ringId = ring?.handoverId;
  // Keyed on the call, not the ring: claiming must not blank the brief.
  const callId = ring?.callId ?? session?.callId ?? null;
  const ringing = !!ring && ring.state === "ringing-portal" && !busy && !session;
  useEffect(() => {
    if (!ringing) { audio.current?.pause(); stopSynthesizedRingtone(); return undefined; }
    let htmlPlayed = false;
    if (audio.current) {
      audio.current.currentTime = 0;
      audio.current?.play().then(() => { htmlPlayed = true; setBlocked(false); })
        .catch(() => { startSynthesizedRingtone(); setBlocked(true); });
    } else {
      startSynthesizedRingtone();
      setBlocked(true);
    }
    const fallbackTimer = setTimeout(() => {
      if (!htmlPlayed || (audio.current && audio.current.paused)) startSynthesizedRingtone();
    }, 400);
    navigator.vibrate?.([200, 100, 200]);
    const title = document.title;
    const flash = setInterval(() => { document.title = document.title === "Incoming call" ? title : "Incoming call"; }, 1000);
    return () => {
      clearTimeout(fallbackTimer);
      clearInterval(flash);
      document.title = title;
      audio.current?.pause();
      stopSynthesizedRingtone();
      navigator.vibrate?.(0);
    };
  }, [ringing, ringId]);

  useEffect(() => {
    setBrief(null);
    if (!callId) return undefined;
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try { const data = await getData(API.HANDOVER_BRIEF(callId)); if (!cancelled) setBrief(data.Content); }
      catch (_) { if (!cancelled) setBrief((current) => current || { error: "Couldn't load the caller brief. You can still answer." }); }
      // Panels land one by one while ringing; once connected they only need topping up.
      if (!cancelled) timer = setTimeout(load, inCall.current ? 10000 : 3000);
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [callId]);

  useEffect(() => {
    if (ring?.state === "ringing-rc" && now - Date.parse(ring.rcDialedAt) > 35000) {
      dismissed.current.add(ring.handoverId);
      setRings((rows) => rows.filter((row) => row.handoverId !== ring.handoverId));
    }
  }, [ring, now]);

  const closeRing = (row) => {
    const target = row || ring;
    if (target) {
      dismissed.current.add(target.handoverId);
      if (String(target.callId) === preferred.current) preferred.current = null;
    }
    setRings((rows) => rows.filter((item) => item.handoverId !== target?.handoverId));
  };
  const leave = (message) => {
    const sdk = device.current; const live = twilioCall.current;
    device.current = null; twilioCall.current = null;
    live?.disconnect?.(); sdk?.destroy();
    setSession(null);
    if (message) toast.info(message);
  };
  const answer = async (mode) => {
    if (!ring || busy) return;
    const claimed = ring;
    setBusy(true);
    try {
      const data = await postData(API.HANDOVER_CLAIM(claimed.callId), { handoverId: claimed.handoverId, mode, phoneNumber: phone });
      // Hold on to the ring row: it is the header fallback while the brief reloads.
      setSession({ handoverId: claimed.handoverId, callId: claimed.callId, ring: claimed, mode,
                   status: mode === "phone" ? "phone" : "connecting", connectedAt: null });
      setExpanded(true);
      closeRing(claimed);
      if (mode === "phone") { toast.info("Your phone is ringing. Answer within 10 seconds."); return; }
      const sdk = new Device(data.Content.token, { logLevel: 1 });
      device.current = sdk;
      const failed = () => { leave(); toast.error("Browser pickup failed. The call will transfer to RingCentral."); };
      sdk.on("error", failed);
      const joined = await sdk.connect({ params: data.Content.params });
      twilioCall.current = joined;
      joined.on("accept", () => setSession((current) => (current ? { ...current, status: "connected", connectedAt: Date.now() } : current)));
      joined.on("disconnect", () => leave("Call ended. Your notes are saved on the call."));
      joined.on("cancel", failed); joined.on("reject", failed);
    } catch (error) {
      leave();
      toast.error(typeof error.Content === "string" ? error.Content : "Unable to answer. RingCentral fallback remains available.");
      if ([409, 410].includes(error.code)) closeRing(claimed);
    } finally { setBusy(false); refresh(); }
  };
  const decline = async () => {
    setBusy(true);
    try { await postData(API.HANDOVER_DECLINE(ring.callId), { handoverId: ring.handoverId }); closeRing(); }
    catch (_) { toast.error("Could not decline. Please retry."); }
    finally { setBusy(false); }
  };
  const enableAudio = () => {
    setBlocked(false);
    unlockAudio().catch(() => {});
    if (audio.current) {
      audio.current.muted = false;
      audio.current?.play().then(() => {
        if (!ringing) { audio.current.pause(); audio.current.currentTime = 0; }
      }).catch(() => {});
    }
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    toast.success("Call alert sounds and notifications enabled.");
  };

  const source = ring || session?.ring || null;
  const caller = brief?.caller || {};
  const transferring = ring?.state === "ringing-rc";
  const seconds = ring ? Math.max(0, Math.ceil((Date.parse(ring.portalDeadline) - now) / 1000)) : 0;
  if (ringId) ringWindow.current[ringId] = Math.max(ringWindow.current[ringId] || 0, seconds);
  const total = (ringId && ringWindow.current[ringId]) || 1;
  const phoneValid = /^\+[1-9]\d{7,14}$/.test(phone);

  return <CallHistoryTheme>
    {/* Fixed, so a blocked-autoplay warning never reflows the page behind it. */}
    {blocked && !ring && !session && <Box sx={{ position: "fixed", left: 16, bottom: 16, zIndex: 1350, maxWidth: 360 }}>
      <Alert severity="info" variant="filled" icon={<VolumeUpIcon />} onClose={() => setBlocked(false)}
             action={<Button size="small" color="inherit" onClick={enableAudio}>Enable</Button>}>
        Turn on call alert sounds
      </Alert>
    </Box>}

    <Dialog open={!!ring && !session} maxWidth="sm" fullWidth aria-labelledby="pickup-title"
            PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.75, color: "primary.contrastText",
                 background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CallIcon sx={{ fontSize: 18 }} />
            <Typography id="pickup-title" variant="overline" sx={{ letterSpacing: 1.2, lineHeight: 1 }}>
              {transferring ? "Transferring to RingCentral" : "Incoming call"}
            </Typography>
          </Stack>
          {!transferring && <Box sx={{ textAlign: "right" }}>
            <Typography variant="h6" sx={{ lineHeight: 1, fontWeight: 700 }}>{seconds}s</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>to answer</Typography>
          </Box>}
        </Stack>
        <CallerIdentity caller={caller} fallbackName={source?.callerName} fallbackNumber={source?.callerNumber} />
        <Box sx={{ mt: 1.25 }}><CallerFacts caller={caller} /></Box>
      </Box>
      <LinearProgress variant={transferring ? "indeterminate" : "determinate"}
                      value={Math.min(100, (seconds / total) * 100)}
                      color={transferring || seconds <= 4 ? "warning" : "primary"} sx={{ height: 3 }} />

      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
        <Stack spacing={1.25}>
          {transferring && <Alert severity="warning" sx={{ py: 0 }}>Nobody answered in time — RingCentral is ringing. You can still take it here.</Alert>}
          {blocked && <Alert severity="info" sx={{ py: 0 }} action={<Button size="small" onClick={enableAudio}>Enable call alert sound</Button>}>
            Your browser blocked the ringtone.
          </Alert>}
          {brief?.error && <Alert severity="warning" sx={{ py: 0 }}>{brief.error}</Alert>}
          <CallerBrief brief={brief} teaser={source?.teaser} callId={callId} handoverId={ringId} height={216} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: 1, borderColor: "divider", display: "block" }}>
        <Collapse in={phoneOpen}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
            <TextField size="small" fullWidth label="Your phone (E.164)" placeholder="+14155550123"
                       value={phone} onChange={(event) => setPhone(event.target.value)} />
            <Button size="small" variant="outlined" startIcon={<SmartphoneIcon />} disabled={busy || !phoneValid}
                    onClick={() => answer("phone")} sx={{ whiteSpace: "nowrap" }}>Ring it</Button>
          </Stack>
        </Collapse>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" color="inherit" startIcon={<SmartphoneIcon />} onClick={() => setPhoneOpen((open) => !open)}>
            Ring my phone
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button color="error" variant="outlined" disabled={busy} onClick={decline} startIcon={<CallEndIcon />}>Decline</Button>
          <Button color="success" variant="contained" disableElevation disabled={busy} onClick={() => answer("browser")}
                  startIcon={<CallIcon />} sx={{ px: 3 }}>Answer in browser</Button>
        </Stack>
      </DialogActions>
    </Dialog>

    {/* On the call: docked, not modal, so the caller's details stay up and the portal keeps working. */}
    <Slide direction="up" in={!!session} mountOnEnter unmountOnExit>
      <Paper elevation={10} sx={{ position: "fixed", right: 16, bottom: 16, zIndex: 1400, borderRadius: 3, overflow: "hidden",
                                 width: { xs: "calc(100% - 32px)", sm: 420 }, maxHeight: "78vh", display: "flex", flexDirection: "column" }}>
        <Box sx={{ px: 2, py: 1.5, color: "primary.contrastText",
                   background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Badge color="success" variant="dot" invisible={session?.status !== "connected"}>
              <PhoneForwardedIcon sx={{ fontSize: 18 }} />
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" sx={{ letterSpacing: 1.2, lineHeight: 1, opacity: 0.85 }}>
                {session?.status === "connected" ? "On the call" : session?.status === "phone" ? "Ringing your phone" : "Connecting"}
              </Typography>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {caller.name || session?.ring?.callerName || "Caller"}
              </Typography>
              <Typography variant="caption" noWrap sx={{ opacity: 0.9 }}>{caller.number || session?.ring?.callerNumber}</Typography>
            </Box>
            {session?.status === "connected" &&
              <Chip size="small" label={clock(session.connectedAt, now)}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "inherit", fontVariantNumeric: "tabular-nums" }} />}
            <Tooltip title={expanded ? "Collapse" : "Caller details and notes"}>
              <IconButton size="small" color="inherit" aria-label={expanded ? "Hide caller details" : "Show caller details"}
                          onClick={() => setExpanded((open) => !open)}>
                {expanded ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        {session?.status !== "connected" && <LinearProgress sx={{ height: 3 }} />}
        <Collapse in={expanded} sx={{ overflowY: "auto" }}>
          <Box sx={{ px: 2, pt: 1.25 }}>
            <CallerFacts caller={caller} dense />
            <Box sx={{ mt: 1.25 }}><CallReason brief={brief} teaser={session?.ring?.teaser} /></Box>
          </Box>
          <Box sx={{ px: 2, pb: 1 }}>
            {/* Notes first: on a live call that is the panel being used. */}
            <CallerBrief brief={brief} teaser={session?.ring?.teaser} callId={session?.callId}
                         handoverId={session?.handoverId} height={200} initialTab={NOTES_TAB} />
          </Box>
        </Collapse>
        <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" color="error" variant="outlined" startIcon={<CallEndIcon />} onClick={() => leave()}>
            {session?.mode === "phone" ? "Close" : "Leave call"}
          </Button>
        </Box>
      </Paper>
    </Slide>
  </CallHistoryTheme>;
}
